import {
  BaseMessageSignerWalletAdapter,
  isVersionedTransaction,
  scopePollingDetectionStrategy,
  WalletAccountError,
  WalletConnectionError,
  WalletDisconnectedError,
  WalletDisconnectionError,
  WalletError,
  WalletNotConnectedError,
  WalletNotReadyError,
  WalletPublicKeyError,
  WalletReadyState,
  WalletSendTransactionError,
  WalletSignMessageError,
  WalletSignTransactionError,
  type WalletName,
} from '@solana/wallet-adapter-base';
import {
  PublicKey,
  type Commitment,
  type Transaction,
  type TransactionSignature,
  type VersionedTransaction,
} from '@solana/web3.js';
import { getBraveProvider } from '../utils/getBraveProvider';
import { BRAVE_WALLET_ICON } from '../config/walletIconAssets';

export const BraveWalletName = 'Brave' as WalletName<'Brave'>;

/**
 * Brave adapter that uses `window.braveSolana` only (not `window.solana`) so Phantom
 * and other wallets cannot hijack Brave Wallet connects.
 *
 * Polls for late provider injection (common on production) and emits readyStateChange
 * so the picker moves Brave from "Install" → "Available".
 */
export class BraveWalletAdapter extends BaseMessageSignerWalletAdapter {
  name = BraveWalletName;
  url = 'https://brave.com/wallet';
  icon = BRAVE_WALLET_ICON;
  supportedTransactionVersions = new Set(['legacy', 0] as const);

  private _readyState =
    typeof window === 'undefined' || typeof document === 'undefined'
      ? WalletReadyState.Unsupported
      : WalletReadyState.NotDetected;
  private _connecting = false;
  private _wallet: NonNullable<ReturnType<typeof getBraveProvider>> | null = null;
  private _publicKey: PublicKey | null = null;

  private _disconnected = () => {
    const wallet = this._wallet;
    if (!wallet) return;

    wallet.off?.('disconnect', this._disconnected);
    wallet.off?.('accountChanged', this._accountChanged);
    this._wallet = null;
    this._publicKey = null;
    this.emit('error', new WalletDisconnectedError());
    this.emit('disconnect');
  };

  private _accountChanged = (...args: unknown[]) => {
    if (!this._publicKey) return;

    try {
      const newPublicKey = args[0] as PublicKey;
      const next = new PublicKey(newPublicKey.toBytes());
      if (this._publicKey.equals(next)) return;
      this._publicKey = next;
      this.emit('connect', next);
    } catch (error) {
      this.emit('error', new WalletPublicKeyError((error as Error)?.message, error));
    }
  };

  constructor() {
    super();

    if (this._readyState === WalletReadyState.Unsupported) return;

    scopePollingDetectionStrategy(() => {
      const installed = getBraveProvider() != null;
      this._readyState = installed ? WalletReadyState.Installed : WalletReadyState.NotDetected;
      this.emit('readyStateChange', this._readyState);
      return installed;
    });
  }

  get publicKey() {
    return this._publicKey;
  }

  get connecting() {
    return this._connecting;
  }

  get connected() {
    return !!this._wallet?.isConnected && !!this._publicKey;
  }

  get readyState(): WalletReadyState {
    return this._readyState;
  }

  async connect(): Promise<void> {
    if (this.connected || this._connecting) return;

    const wallet = getBraveProvider();
    if (!wallet) {
      throw new WalletNotReadyError();
    }

    try {
      this._connecting = true;

      if (!wallet.isConnected) {
        try {
          await wallet.connect();
        } catch (error) {
          throw new WalletConnectionError((error as Error)?.message, error);
        }
      }

      if (!wallet.publicKey) {
        throw new WalletAccountError();
      }

      let publicKey: PublicKey;
      try {
        publicKey = new PublicKey(wallet.publicKey.toBytes());
      } catch (error) {
        throw new WalletPublicKeyError((error as Error)?.message, error);
      }

      wallet.on?.('disconnect', this._disconnected);
      wallet.on?.('accountChanged', this._accountChanged);
      this._wallet = wallet;
      this._publicKey = publicKey;
      this.emit('connect', publicKey);
    } catch (error) {
      const walletError =
        error instanceof WalletError
          ? error
          : new WalletConnectionError((error as Error)?.message, error);
      this.emit('error', walletError);
      throw walletError;
    } finally {
      this._connecting = false;
    }
  }

  async disconnect(): Promise<void> {
    const wallet = this._wallet;
    if (wallet) {
      wallet.off?.('disconnect', this._disconnected);
      wallet.off?.('accountChanged', this._accountChanged);
      this._wallet = null;
      this._publicKey = null;
      try {
        await wallet.disconnect();
      } catch (error) {
        this.emit('error', new WalletDisconnectionError((error as Error)?.message, error));
      }
    }
    this.emit('disconnect');
  }

  async sendTransaction(
    transaction: Transaction | VersionedTransaction,
    connection: Parameters<BaseMessageSignerWalletAdapter['sendTransaction']>[1],
    options: Parameters<BaseMessageSignerWalletAdapter['sendTransaction']>[2] = {}
  ): Promise<TransactionSignature> {
    // Sign via Brave, submit via app RPC so cluster matches VITE_CWY_NETWORK / Helius.
    try {
      const { signers, ...sendOptions } = options as {
        signers?: Parameters<Transaction['partialSign']>;
      } & Record<string, unknown>;

      if (!isVersionedTransaction(transaction)) {
        transaction = (await this.prepareTransaction(transaction, connection, sendOptions)) as Transaction;
        if (signers?.length) transaction.partialSign(...signers);
      } else if (signers?.length) {
        transaction.sign(signers as never);
      }

      const signed = await this.signTransaction(transaction);
      const raw = signed.serialize();
      return connection.sendRawTransaction(raw, {
        skipPreflight: sendOptions?.skipPreflight as boolean | undefined,
        preflightCommitment:
          (sendOptions?.preflightCommitment as Commitment | undefined) ??
          connection.commitment ??
          'confirmed',
      });
    } catch (error) {
      if (error instanceof WalletError) throw error;
      throw new WalletSendTransactionError((error as Error)?.message, error);
    }
  }

  async signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> {
    const wallet = this._wallet;
    if (!wallet) throw new WalletNotConnectedError();

    try {
      if (!wallet.signTransaction) {
        throw new WalletSignTransactionError('Brave Wallet signTransaction is unavailable');
      }
      return (await wallet.signTransaction(transaction)) || transaction;
    } catch (error) {
      throw new WalletSignTransactionError((error as Error)?.message, error);
    }
  }

  async signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]> {
    const wallet = this._wallet;
    if (!wallet) throw new WalletNotConnectedError();

    try {
      if (!wallet.signAllTransactions) {
        throw new WalletSignTransactionError('Brave Wallet signAllTransactions is unavailable');
      }
      return (await wallet.signAllTransactions(transactions)) || transactions;
    } catch (error) {
      throw new WalletSignTransactionError((error as Error)?.message, error);
    }
  }

  async signMessage(message: Uint8Array): Promise<Uint8Array> {
    const wallet = this._wallet;
    if (!wallet) throw new WalletNotConnectedError();

    try {
      const { signature } = await wallet.signMessage(message, 'utf8');
      return signature;
    } catch (error) {
      throw new WalletSignMessageError((error as Error)?.message, error);
    }
  }
}
